import {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createSpecialistClientContractByClient,
  fetchLastContractBySpecialistId,
  getUrlSpecialistContractPdfById,
} from '../../api/contracts';
import useToast from '../ToastContext/ToastContext';
import { Browser } from '@capacitor/browser';
import { useDispatch } from 'react-redux';
import { updateConsultationBy } from '../../redux/consultations/reducer';
import { updateSpecialistBy } from '../../redux/specialists/reducer';
import { updateUsersSpecialistBy } from '../../redux/usersPsychologists/reducer';
import { ClientSpecialistContractStatusEnum, ContractStatusEnum } from '@mindly/ui-components';

type ModalType = 'booking' | 'schedule' | 'update-contract' | null;

type SignAgreementsContextType = {
  bookingPresentingElement: MutableRefObject<HTMLElement | null>;
  schedulePresentingElement: MutableRefObject<HTMLElement | null>;
  updateContractPresentingElement: MutableRefObject<HTMLElement | null>;
  createContractBySpecialistId: (specialistId: string) => Promise<any>;
  openModal: (modalType: ModalType) => void;
  closeModal: () => void;
  isSignAgreementOpen: boolean;
  currentOpenModal: ModalType;
  fetchStatus: 'idle' | 'pending' | 'error' | 'success';
  openContractById: (id: string, specialistId: string, lang: string) => Promise<void>;
  hasNeedToSignContract: (
    mindlyContractsStatus: ContractStatusEnum,
    specialistClientContractStatus?: ClientSpecialistContractStatusEnum | null
  ) => boolean;
};

export const MapStatusContractToUIStatus: Record<
  ClientSpecialistContractStatusEnum,
  'active' | 'pending' | 'not_signed' | 'expired'
> = {
  [ClientSpecialistContractStatusEnum.active]: 'active',
  [ClientSpecialistContractStatusEnum.signed_by_specialist]: 'not_signed',
  [ClientSpecialistContractStatusEnum.signed_by_client]: 'pending',
  [ClientSpecialistContractStatusEnum.expired]: 'expired',
};

const SignAgreementsContext = createContext<SignAgreementsContextType>({
  schedulePresentingElement: { current: null } as MutableRefObject<HTMLElement | null>,
  bookingPresentingElement: { current: null } as MutableRefObject<HTMLElement | null>,
  updateContractPresentingElement: { current: null } as MutableRefObject<HTMLElement | null>,
  openModal: () => {}, // no-op function
  closeModal: () => {}, // no-op function
  isSignAgreementOpen: false,
  currentOpenModal: null,
  createContractBySpecialistId: async () => {},
  openContractById: async () => {},
  hasNeedToSignContract: () => false,
  fetchStatus: 'idle',
});

type SignAgreementsProviderProps = {};

export function SignAgreementsProvider({ children }: PropsWithChildren<SignAgreementsProviderProps>) {
  const schedulePresentingElement = useRef<HTMLElement | null>(null);
  const bookingPresentingElement = useRef<HTMLElement | null>(null);
  const { showToast } = useToast();
  const updateContractPresentingElement = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();
  const [fetchStatus, setFetchStatus] = useState<'idle' | 'pending' | 'error' | 'success'>('idle');
  const [isSignAgreementOpen, setIsSignAgreementOpen] = useState(false);
  const [currentOpenModal, setCurrentOpenModal] = useState<ModalType>(null);

  const openModal = (modalType: ModalType) => {
    setCurrentOpenModal(modalType);
    setIsSignAgreementOpen(true);
  };

  const closeModal = () => {
    setIsSignAgreementOpen(false);
    setCurrentOpenModal(null);
  };

  const hasNeedToSignContract = useCallback(
    (
      mindlyContractsStatus: ContractStatusEnum,
      specialistClientContractStatus?: ClientSpecialistContractStatusEnum | null
    ) => {
      if (!mindlyContractsStatus || mindlyContractsStatus !== ContractStatusEnum.active) {
        return false;
      }

      return !specialistClientContractStatus;
    },
    []
  );

  const openContractById = useCallback(async (id: string, specialistId: string, lang: string) => {
    try {
      setFetchStatus('pending');
      const url = await getUrlSpecialistContractPdfById(id, specialistId, lang);
      await Browser.open({
        url,
        presentationStyle: 'fullscreen',
        windowName: '_blank',
      });
      setFetchStatus('success');
    } catch (error) {
      setFetchStatus('error');
      showToast({
        error,
      });
    }
  }, []);

  const createContractBySpecialistId = useCallback(
    async (specialistId: string) => {
      setFetchStatus('pending');
      try {
        let data = await fetchLastContractBySpecialistId(specialistId);

        if (!data) {
          data = await createSpecialistClientContractByClient(specialistId);
        }

        if (data) {
          dispatch(
            updateConsultationBy({
              field: 'psychologist_id',
              value: specialistId,
              data: {
                client: {
                  specialist_client_contract_status: data.status,
                  specialist_client_contract_id: data.id,
                },
              },
            })
          );
          dispatch(
            updateSpecialistBy({
              field: 'id',
              value: specialistId,
              data: {
                client: {
                  specialist_client_contract_status: data.status,
                  specialist_client_contract_id: data.id,
                },
              },
            })
          );
          dispatch(
            updateUsersSpecialistBy({
              field: 'id',
              value: specialistId,
              data: {
                client: {
                  specialist_client_contract_status: data.status,
                  specialist_client_contract_id: data.id,
                },
              },
            })
          );
        }

        setFetchStatus('success');
        return data;
      } catch (error) {
        setFetchStatus('error');
        showToast({
          error,
        });
      }
    },
    [showToast]
  );

  const value = useMemo(() => {
    return {
      schedulePresentingElement,
      bookingPresentingElement,
      updateContractPresentingElement,
      openModal,
      closeModal,
      isSignAgreementOpen,
      currentOpenModal,
      createContractBySpecialistId,
      fetchStatus,
      openContractById,
      hasNeedToSignContract,
    };
  }, [
    schedulePresentingElement,
    bookingPresentingElement,
    updateContractPresentingElement,
    openModal,
    closeModal,
    isSignAgreementOpen,
    currentOpenModal,
    createContractBySpecialistId,
    fetchStatus,
    openContractById,
    hasNeedToSignContract,
  ]);

  return <SignAgreementsContext.Provider value={value}>{children}</SignAgreementsContext.Provider>;
}

export const useSignAgreementsContext = () => {
  return useContext(SignAgreementsContext);
};
